import { FeatureFlag } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import type { PokerSpinAndGo } from '@ps/web-martech-cms-types';
import type { RawGame } from '@ps/web-poker-tile-mappers';
import { mappers } from '@ps/web-poker-tile-mappers';
import type {
  DbmImageResult,
  GetSpinDataRequest,
  GetWatchGamesRequest
} from '@ps/web-shared-api-types/v1-preview/SwPkrSpinAndGo';
import * as Sentry from '@sentry/react';
import type { Subscription } from 'rxjs';
import { firstValueFrom, merge } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';

import { State, connect } from '@swf/core/api/connect';
import type { WidgetEffectParams } from '@swf/core/store';
import { currencyChange, login, logout } from '@swf/core/utils/actions';

import { ENDPOINTS } from '../constants';
import type { UpdateType } from '../types/Game';
import type { WatchGamesResponse } from '../types/WatchGames';

import type { WidgetDispatch, WidgetSelect } from './types';

let spinDataSubscription: Subscription | undefined;
let watchGamesSubscription: Subscription | undefined;

const clearSubscriptions = () => {
  spinDataSubscription = undefined;
  watchGamesSubscription = undefined;
};

const isWsConnectionActive = async () => {
  const client = await firstValueFrom(connect());
  return client.state === State.CONNECTED && client.client?.connected;
};

export const effects = {
  async preload(
    _payload: undefined,
    { actions, dispatch, rootState, select, widget }: WidgetEffectParams<PokerSpinAndGo>
  ): Promise<void> {
    const isWatchGamesEnabled = select.feature.check(
      rootState,
      FeatureFlag.PKRSpinAndGoWatchGamesVisibility
    );

    const {
      clear,
      gamesRequested,
      subscribeToWatchGames,
      subscribeToSpinGames,
      clearGames
    }: WidgetDispatch = dispatch[widget.uid];
    const { hasPreloaded }: WidgetSelect = select[widget.uid];
    if (!hasPreloaded(rootState)) {
      await gamesRequested();
    }

    if (canUseDOM) {
      if (!spinDataSubscription) subscribeToSpinGames();
      if (isWatchGamesEnabled && !watchGamesSubscription) subscribeToWatchGames();
    }

    const currencyChangeObservable = currencyChange(actions);
    const loginObservable = login(actions);
    const logoutObservable = logout(actions);

    if (currencyChangeObservable && loginObservable && logoutObservable) {
      merge(currencyChangeObservable, loginObservable, logoutObservable)
        .pipe(throttleTime(100))
        .subscribe(async (subscribeData) => {
          if (subscribeData.type === 'currency/set') {
            clearGames();
          }

          const isWsActive = await isWsConnectionActive();
          if (!isWsActive) {
            gamesRequested();
          }

          if (isWatchGamesEnabled) subscribeToWatchGames();
          subscribeToSpinGames();
        });
    }

    widget.unmount.subscribe(() => {
      clear();
      clearSubscriptions();
    });
  },
  async gamesRequested(
    _payload: undefined,
    { dispatch, select, widget, request, rootState }: WidgetEffectParams<PokerSpinAndGo>
  ): Promise<void> {
    const { gamesLoadSuccess, gamesLoadFailure }: WidgetDispatch = dispatch[widget.uid];
    const widgetSelect: WidgetSelect = select[widget.uid];
    const { locale, isPlayMoney, site, brand, country } = widgetSelect.getRequestData(rootState);
    const requestBody: GetSpinDataRequest = {
      isRealMoney: !isPlayMoney,
      country,
      brand,
      site
    };

    try {
      const { body } = await request<RawGame[]>({
        service: ENDPOINTS.spinData,
        http: true,
        body: requestBody
      });

      const mappedGames = mappers.mapSagGames({
        sagGames: body,
        locale,
        isPlayMoney
      });
      gamesLoadSuccess(mappedGames);
    } catch {
      gamesLoadFailure();
    }
  },
  async subscribeToWatchGames(
    _payload: undefined,
    {
      actions,
      dispatch,
      select,
      widget,
      rootState,
      requestStream
    }: WidgetEffectParams<PokerSpinAndGo>
  ): Promise<void> {
    const { updateWatchGames }: WidgetDispatch = dispatch[widget.uid];
    const options = select.cms.getRequestOptions(rootState);
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;
    const onLogin = login(actions) as NonNullable<ReturnType<typeof login>>;
    const widgetSelect: WidgetSelect = select[widget.uid];
    const { isPlayMoney, site, brand, country } = widgetSelect.getRequestData(rootState);
    const requestBody: GetWatchGamesRequest = {
      isRealMoney: !isPlayMoney,
      country,
      brand,
      site
    };

    if (widget.watchgames_filter_variables?.buy_in)
      requestBody.buyIn = widget.watchgames_filter_variables.buy_in;

    if (widget.watchgames_filter_variables?.game_variant)
      requestBody.gameVariants = widget.watchgames_filter_variables
        .game_variant as GetWatchGamesRequest['gameVariants'];

    if (watchGamesSubscription && !watchGamesSubscription.closed)
      watchGamesSubscription.unsubscribe();

    watchGamesSubscription = requestStream<WatchGamesResponse>({
      service: ENDPOINTS.watchGamesSubscriptionV2,
      body: requestBody,
      ...options
    })
      .pipe(
        takeUntil(currencyChange(actions) as typeof actions),
        takeUntil(onLogout),
        takeUntil(onLogin)
      )
      .subscribe(({ data }) =>
        updateWatchGames({
          watchGames: data,
          availableTabs: widget.watchgames_filter_variables?.tabs
        })
      );
  },
  async avatarImageRequest(
    payload: string[],
    { request, dispatch, widget }: WidgetEffectParams<PokerSpinAndGo>
  ): Promise<void> {
    const { updateAvatarImages }: WidgetDispatch = dispatch[widget.uid];
    const requestBody = {
      imageIds: payload
    };

    try {
      const { body } = await request({
        service: ENDPOINTS.avatarImages,
        http: true,
        body: requestBody
      });

      const avatarImages = payload.map(
        (imageId) =>
          body.images.find((obj: DbmImageResult) => obj.imageId === parseInt(imageId)) || {
            imageId: parseInt(imageId),
            base64Image: ''
          }
      );

      updateAvatarImages(avatarImages);
    } catch (err) {
      Sentry.captureException(err);
    }
  },
  async subscribeToSpinGames(
    _payload: undefined,
    { dispatch, select, widget, rootState, requestStream }: WidgetEffectParams<PokerSpinAndGo>
  ) {
    const { updateGames }: WidgetDispatch = dispatch[widget.uid];
    const options = select.cms.getRequestOptions(rootState);
    const widgetSelect: WidgetSelect = select[widget.uid];
    const { locale, isPlayMoney, site, brand, country } = widgetSelect.getRequestData(rootState);
    const requestBody: GetSpinDataRequest = {
      isRealMoney: !isPlayMoney,
      country,
      brand,
      site
    };

    if (spinDataSubscription && !spinDataSubscription.closed) spinDataSubscription.unsubscribe();

    spinDataSubscription = requestStream<{ data: { list: RawGame[]; operationType: UpdateType } }>({
      service: ENDPOINTS.spinDataSubscription,
      body: requestBody,
      ...options
    }).subscribe((data) => {
      if (data.data && data.data.list) {
        updateGames({
          games: data.data.list,
          operationType: data.data.operationType,
          locale,
          isPlayMoney
        });
      }
    });
  }
};
